import {action, computed, makeObservable, observable, runInAction} from "mobx";
import BackApplicationsPageStore from "../../pages/BackApplicationsPageStore";
import {parseStatusCsv, ParsedStatusRow, ParseStatusCsvInvalid} from "../../../../helpers/csv/parseStatusCsv";
import ApiBackCampaignsApplicationsSetStatusByEmailPost, {
  SetStatusByEmailItem,
  SetStatusByEmailReport
} from "../../../../services/api/back/campaigns/applications/ApiBackCampaignsApplicationsSetStatusByEmailPost";
import ApiApplicationStatus from "../../../../services/api/back/campaigns/applications/ApiApplicationStatus";
import toastsStore from "../../../singletones/ToastsStore";

export type BulkStatusImportState = "init" | "ready" | "posting" | "done" | "error";

// Клиентский предел размера файла. Бэк всё равно режет тело запроса (DATA_UPLOAD_MAX_MEMORY_SIZE),
// но без этой проверки юзер получил бы невнятную ошибку уже после долгого аплоада.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

class BulkStatusImportModalStore {
  readonly modal = "BulkStatusImportModal";

  state: BulkStatusImportState = "init";
  fileName = "";
  rows: ParsedStatusRow[] = [];
  parseInvalid: ParseStatusCsvInvalid[] = [];
  // Явный выбор пользователя; пустая строка = «не выбрано», тогда defaultStatus автоподставляет статус.
  private _defaultStatus = "";
  report?: SetStatusByEmailReport;
  error?: string;

  constructor(private root: BackApplicationsPageStore) {
    makeObservable<BulkStatusImportModalStore, "_defaultStatus">(this, {
      state: observable,
      fileName: observable,
      rows: observable,
      parseInvalid: observable,
      _defaultStatus: observable,
      report: observable,
      error: observable,
      availableStates: computed,
      defaultStatus: computed,
      rowsWithStatusCount: computed,
      canSubmit: computed,
      setDefaultStatus: action,
      loadFile: action,
      submit: action,
    });
  }

  get availableStates(): ApiApplicationStatus[] {
    return this.root.applicationsConfig?.available_states ?? [];
  }

  // Реактивный дефолт: пока пользователь не выбрал статус вручную, берём "approved" (или первый доступный).
  // Важно, что это computed, а не присвоение в конструкторе: если конфиг кампании ещё не подгрузился к
  // моменту открытия модалки, значение само подтянется, как только available_states станут доступны
  // (иначе кнопка Apply залипала бы в disabled из-за пустого defaultStatus).
  get defaultStatus(): string {
    if (this._defaultStatus) {
      return this._defaultStatus;
    }

    const approved = this.availableStates.find(s => s.tag === "approved");

    return approved ? approved.tag : (this.availableStates[0]?.tag ?? "");
  }

  get rowsWithStatusCount(): number {
    return this.rows.filter(r => !!r.status).length;
  }

  get canSubmit(): boolean {
    return this.state !== "posting" && this.rows.length > 0 && !!this.defaultStatus;
  }

  setDefaultStatus(tag: string) {
    this._defaultStatus = tag;
  }

  async loadFile(file: File) {
    runInAction(() => {
      this.error = undefined;
      this.report = undefined;
      this.fileName = file.name;
    });

    // FE-валидация до парсинга: понятное сообщение вместо «битого» результата ниже по потоку.
    const looksCsv = /\.csv$/i.test(file.name) || file.type === "text/csv" || file.type === "";
    if (!looksCsv) {
      this.failLoad("Please upload a .csv file.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.failLoad(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 5 MB.`);
      return;
    }

    try {
      const text = await this.readFileText(file);
      const {rows, invalid} = parseStatusCsv(text);

      runInAction(() => {
        this.rows = rows;
        this.parseInvalid = invalid;
        this.state = "ready";
      });
    } catch (e) {
      this.failLoad("Failed to read the file");
    }
  }

  private failLoad(message: string) {
    runInAction(() => {
      this.rows = [];
      this.parseInvalid = [];
      this.error = message;
      this.state = "init";
    });
  }

  async submit() {
    if (!this.root.backAppStore.campaignId || !this.canSubmit) {
      return;
    }

    this.state = "posting";
    this.error = undefined;

    try {
      const response = await ApiBackCampaignsApplicationsSetStatusByEmailPost(this.root.backAppStore.campaignId, {
        default_status: this.defaultStatus,
        items: this.rows.map(r => {
          const item: SetStatusByEmailItem = {email: r.email};
          if (r.status) {
            item.status = r.status;
          }
          if (r.level) {
            item.level = r.level;
          }
          if (r.program) {
            item.program = r.program;
          }
          if (r.direction) {
            item.direction = r.direction;
          }
          return item;
        }),
      });

      runInAction(() => {
        this.report = response.data;
        this.state = "done";
      });

      // Обновляем таблицу, чтобы изменившиеся статусы подтянулись, и показываем сводку.
      this.root.refresh();

      const summary = response.data.summary;
      toastsStore.addToast({
        props: {autohide: true, delay: 6000},
        // Предупреждения (несопоставленные program/direction) подсвечиваем даже при успешной смене статуса.
        variant: summary.changed > 0 && summary.warnings === 0 ? "success" : "warning",
        header: "Status import finished",
        body: `Changed ${summary.changed}, warnings ${summary.warnings}, skipped ${summary.skipped}, not found ${summary.not_found}, invalid ${summary.invalid}`,
      });
    } catch (e) {
      runInAction(() => {
        this.error = "Failed to import statuses. Try again later.";
        this.state = "error";
      });
    }
  }

  private readFileText(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(decodeCsvBuffer(reader.result as ArrayBuffer));
      reader.onerror = () => reject(reader.error);
      // Читаем байты, а не текст: так можем сами выбрать кодировку (UTF-8 vs Windows-1251),
      // не полагаясь на эвристику FileReader.readAsText (см. decodeCsvBuffer).
      reader.readAsArrayBuffer(file);
    });
  }
}

/**
 * Декодирует байты CSV в строку. Сначала пробуем строгий UTF-8; если файл не валидный UTF-8
 * (типичный экспорт из Excel на русской локали — Windows-1251), откатываемся на cp1251. Это
 * чинит «кракозябры» в кириллических именах статусов без выбора кодировки руками.
 */
export function decodeCsvBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);

  try {
    return new TextDecoder("utf-8", {fatal: true}).decode(bytes);
  } catch {
    try {
      return new TextDecoder("windows-1251").decode(bytes);
    } catch {
      return new TextDecoder("utf-8").decode(bytes); // последний шанс — без fatal
    }
  }
}

export default BulkStatusImportModalStore;
